<!--
 * @Description: 
 * @Author: luckymiaow
 * @Date: 2022-12-19 15:42:37
 * @LastEditors: luckymiaow
-->
<script setup lang="ts">
import type { PropType } from 'vue';
import { onMounted, ref, useSlots, watchEffect } from 'vue';
import type { PaginationProps } from 'ant-design-vue';
import { Button as AButton, List as AList, message } from 'ant-design-vue';
import useSlot from '../../hooks/useSlot';
import type { Api, Page } from '../types';

interface Props {
  api?: Api;
  getParams?: any;
  postParams?: any;
  page?: Page;
  immediate?: boolean;
  pagination?: PropType<false | PaginationProps>;
  dataSource?: Array<unknown>;
  loadMore?: boolean;
}

const props = withDefaults(defineProps<Props>(), {});

const emits = defineEmits<{
  (event: 'update:data-source', data: any): void;
  (event: 'pageChange', page: number, pageSize: number): void;
}>();

const { useExcludeSlots } = useSlot({ slots: useSlots() }, []);

const dataSource_in = ref<Array<unknown>>([]);

const spinning = ref(false);

const loadFinish = ref(false);

const page_in = ref({ limit: 10, offset: 0 });

const apiIsPageType = ref(true); // api是分页

const pagination = ref<false | PaginationProps>(props.pagination as any);

function pageChange(page: number, pageSize: number) {
  if (pagination.value) {
    pagination.value.pageSize = pageSize;
    pagination.value.current = page;
    page_in.value.offset = (page - 1) * pageSize!;
    page_in.value.limit = pageSize;
    getData();
  }
  emits('pageChange', page, pageSize);
}

function initPagination() {
  if (pagination.value) {
    if (page_in.value.offset === 0) pagination.value.current = 1;
    pagination.value.showTotal = (total: number) =>
      `共${total}条记录 第${pagination.value.current}/${Math.ceil(total / pagination.value.pageSize!)}页`;
  }
}

async function getData(perception = true) {
  if (pagination.value != null) initPagination();
  if (props.api != null) {
    if (perception) spinning.value = true;
    try {
      const data = await props.api({ ...props.getParams, ...page_in.value }, props.postParams!);
      if ('items' in data) {
        if (pagination.value == null) {
          pagination.value = {
            current: 1,
            total: 0,
            pageSize: 10,
            pageSizeOptions: ['8', '10', '20', '40'],
            showSizeChanger: true,
            showQuickJumper: true,
            onChange: pageChange,
          };
        }
        pagination.value.pageSize = page_in.value.limit ?? 10;
        pagination.value.total = data?.totals ?? 0;
        apiIsPageType.value = true;
        if (props.loadMore) {
          if (dataSource_in.value == null) dataSource_in.value = [];
          dataSource_in.value.push(...data.items!);
        } else {
          dataSource_in.value = data.items!;
        }

        if (data.offset + data.limit >= data.totals! || dataSource_in.value.length === data.totals) {
          loadFinish.value = true;
        }
        emits('update:data-source', dataSource_in.value ?? []);
      } else if (Array.isArray(data)) {
        apiIsPageType.value = false;
        dataSource_in.value = data;
        emits('update:data-source', data);
      }
    } catch (err: any) {
      message.error({ content: err?.message ? err?.message : err.toString() });
    } finally {
      spinning.value = false;
    }
  }
}

async function refreshData(perception = false) {
  await getData(perception);
}

async function fetchData() {
  // if (props.loadMore === false) return;
  if (pagination.value) {
    pagination.value.current = 1;
    page_in.value.offset = 0;
  }
  await getData();
}

function onLoadMore() {
  pageChange(pagination.value!.current! + 1, pagination.value!.pageSize!);
}

defineExpose({ fetchData, refreshData, onLoadMore, dataSource: dataSource_in });

watchEffect(() => {
  dataSource_in.value = props.dataSource!;
});

onMounted(async () => {
  if (props.immediate) {
    await getData();
    initPagination();
  }
});
</script>

<script lang="ts">
export default {
  name: 'List',
};
</script>

<template>
  <AList :data-source="dataSource_in" :loading="loadMore ? false : spinning" :pagination="loadMore ? null : pagination">
    <template v-for="(slot, key) in useExcludeSlots" :key="key" #[key]="scope">
      <slot
        :name="key"
        v-bind="scope ?? {}"
        :serial-number="
          !loadMore && pagination ? (pagination.current - 1) * pagination?.pageSize + scope.index + 1 : scope.index + 1
        "
      >
      </slot>
    </template>

    <template v-if="loadMore" #loadMore>
      <slot name="loadMore" :loading="spinning" :loadFinish="loadFinish">
        <div
          v-if="!loadFinish && !spinning"
          :style="{ textAlign: 'center', marginTop: '12px', height: '32px', lineHeight: '32px' }"
        >
          <a-button @click="onLoadMore">{{ spinning ? '加载中' : '加载更多' }}</a-button>
        </div>
      </slot>
    </template>
  </AList>
</template>
